import { DefaultMantineColor, Progress } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ReactNode, useEffect, useState } from "react";

export class Utils {
	public static generateGUID(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	private static notificationStyle = {
		top: "5vh"
	};

	// A simple active notifications tracker
	private static activeNotifications = new Set<string>();

	public static displayNotification(
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		id?: string,
		onClose?: () => void
	): string {
		// Use the NotificationContent component only when autoClose is a number.
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);


		if (id !== undefined) {
			if (Utils.activeNotifications.has(id)) {
				Utils.updateNotification(id, title, message, color, icon, withCloseButton, autoClose);
				return id;
			} else {
				Utils.activeNotifications.add(id);
			}
		}
		if (icon === "loading") {
			return notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		} else {
			return notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		}
	}

	public static updateNotification(
		id: string,
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		onClose?: () => void
	) {
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);

		if (!Utils.activeNotifications.has(id)) {
			Utils.activeNotifications.add(id);
		}

		if (icon === "loading") {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		} else {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				loading: false,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		}
	}
}

interface NotificationContentProps {
	message?: ReactNode;
	time: number;
}

const NotificationContent: React.FC<NotificationContentProps> = ({ message, time }) => {
	const [progress, setProgress] = useState(100);

	useEffect(() => {
		const startTime = Date.now();
		const timer = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const percentage = Math.max(0, Math.min(100, 100 - ((elapsed / time) * 100)));
			setProgress(percentage);
		}, 50); // update every 50ms
		return () => clearInterval(timer);
	}, [time]);

	return (
		<>
			{message}
			{/* Render the progress bar only if autoClose is a number */}
			<Progress
				value={progress}
				size="xs"
				mt={8}
				color="yellow"
			/>
		</>
	);
};
